import { matchPath } from 'react-router-dom'
import { createSelector } from 'reselect'
import { parseQuery } from '@calendly/http-tools'

import { inviteeSelector } from 'selectors/invitee'
import buildConfirmationExternalUrl from 'services/confirmation/build_external_url'
import { eventTypeSelector } from 'root-api'
import { trackingSelector } from 'selectors/tracking'
import { currentInviteeSelector } from 'selectors/current_invitee'

const BOOKING_METHODS = {
  instant: 'instant',
  poll: 'poll',
}

const TRACKING_URL_KEYS = [
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_content',
  'utm_term',
  'salesforce_uuid'
]

const pathWithQuery = (path) => (
  `${path}${window.location.search}`
)

const isAdhocSelector = () => [
  matchPath(window.location.pathname, { path: '/b/:profileSlug/:eventTypeSlug/:spotStartTime', exact: true }),
  matchPath(window.location.pathname, { path: '/b/:profileSlug/:eventTypeSlug', exact: true })
].find(Boolean)

const adhocPathSelector = createSelector(
  isAdhocSelector,
  ({ params: { profileSlug, eventTypeSlug } }) => `/b/${profileSlug}/${eventTypeSlug}`
)

const isOrgEventTypeSelector = () => [
  matchPath(window.location.pathname, { path: '/c/:eventTypeUuid/invitees/:inviteeUuid', exact: true }),
  matchPath(window.location.pathname, { path: '/c/:eventTypeUuid/:spotStartTime', exact: true }),
  matchPath(window.location.pathname, { path: '/c/:eventTypeUuid', exact: true })
].find(Boolean)

const orgEventTypePathSelector = createSelector(
  isOrgEventTypeSelector,
  ({ params: { eventTypeUuid } }) => `/c/${eventTypeUuid}`
)

const ownedEventTypeParamsSelector = () => [
  matchPath(window.location.pathname, { path: '/:profileSlug/:eventTypeSlug/invitees/:inviteeUuid', exact: true }),
  matchPath(window.location.pathname, { path: '/:profileSlug/:eventTypeSlug/:spotStartTime', exact: true }),
  matchPath(window.location.pathname, { path: '/:profileSlug/:eventTypeSlug', exact: true })
].find(Boolean)

const ownedEventTypePathSelector = createSelector(
  ownedEventTypeParamsSelector,
  ({ params: { profileSlug, eventTypeSlug } }) => `/${profileSlug}/${eventTypeSlug}`
)

const schedulingLinkParamsSelector = () => [
  matchPath(window.location.pathname, { path: '/d/:linkUid/:eventTypeSlug/invitees/:inviteeUuid', exact: true }),
  matchPath(window.location.pathname, { path: '/d/:linkUid/:eventTypeSlug/:spotStartTime', exact: true }),
  matchPath(window.location.pathname, { path: '/d/:linkUid/:eventTypeSlug', exact: true }),
  matchPath(window.location.pathname, { path: '/d/:linkUid', exact: true })
].find(Boolean)

const schedulingLinkPathSelector = createSelector(
  schedulingLinkParamsSelector,
  ({ params: { linkUid, eventTypeSlug } }) => `/d/${linkUid}/${eventTypeSlug || '-'}`
)

const isReschedulingSelector = () => [
  matchPath(window.location.pathname, { path: '/reschedulings/:inviteeUuid/confirmation', exact: true }),
  matchPath(window.location.pathname, { path: '/reschedulings/:inviteeUuid/:spotStartTime', exact: true }),
  matchPath(window.location.pathname, { path: '/reschedulings/:inviteeUuid', exact: true })
].some(Boolean)

const reschedulingPathSelector = createSelector(
  inviteeSelector,
  ({ uuid }) => `/reschedulings/${uuid}`
)

const isMeetingPollSelector = createSelector(
  eventTypeSelector,
  (eventType) => eventType.booking_method === BOOKING_METHODS.poll
)

export const isPopupTextEmbedTypeSelector = () => parseQuery(window.location.search).embed_type === 'PopupText'

export const eventTypePathSelector = (state, includeQuery) => {
  let path
  if (schedulingLinkParamsSelector(state)) {
    path = schedulingLinkPathSelector(state)
  } else if (isAdhocSelector(state)) {
    path = adhocPathSelector(state)
  } else if (isOrgEventTypeSelector(state)) {
    path = orgEventTypePathSelector(state)
  } else if (isReschedulingSelector(state)) {
    path = reschedulingPathSelector(state)
  } else {
    path = ownedEventTypePathSelector(state)
  }

  return includeQuery ? pathWithQuery(path) : path
}

const trackingUrlKeys = (object) => Object.keys(object)
  .filter((key) => TRACKING_URL_KEYS.includes(key))
  .reduce((obj, key) => {
    obj[key] = object[key]
    return obj
  }, {})

export const externalConfirmationPathSelector = (state, { formData }) => {
  const event = { ...formData, ...state.scheduling.event }

  return buildConfirmationExternalUrl(
    eventTypeSelector(state),
    event,
    state.scheduling.invitee,
    trackingUrlKeys(trackingSelector(state))
  )
}

// Helper function to build rescheduling confirmation path from invitee UUID
const buildReschedulingConfirmationPath = (inviteeUuid) => (
  ['', 'reschedulings', inviteeUuid, 'confirmation'].join('/')
)

const reschedulingConfirmationPathSelector = (state) => {
  const currentInvitee = currentInviteeSelector(state)
  return currentInvitee?.uuid ? buildReschedulingConfirmationPath(currentInvitee.uuid) : null
}

const schedulingConfirmationPathSelector = (state) => {
  const params = new URLSearchParams(window.location.search)

  if (isMeetingPollSelector(state)) {
    const spots = encodeURIComponent(params.get('spots'))

    return `${eventTypePathSelector(state)}/confirmation?spots=${spots}`
  }

  const basePath = [eventTypePathSelector(state), 'invitees', state.scheduling.invitee.uuid].join('/')
  const redeem = params.get('redeem')

  return redeem ? `${basePath}?redeem=${encodeURIComponent(redeem)}` : basePath
}

export const internalConfirmationPathSelector = (state) => (
  currentInviteeSelector(state)
    ? reschedulingConfirmationPathSelector(state)
    : schedulingConfirmationPathSelector(state)
)
